import {isFeatureEnabled} from 'feature-flags';
import {Heading, HStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {head, isEmpty, isNotNil} from 'ramda';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {COUNTRY_CZE, formatDocumentTitle} from '@dms/shared';

import {AuctionPreview} from '../../../components/AuctionPreview/AuctionPreview';
import {SourcingDataStatus} from '../../../components/SourcingDataStatus/SourcingDataStatus';
import {SourcingLayout} from '../../../components/SourcingLayout/SourcingLayout';
import {useSourcingTable} from '../../../components/SourcingTable/hooks/useSourcingTable';
import {useSourcingTableColumns} from '../../../components/SourcingTable/hooks/useSourcingTableColumns';
import {SourcingTable} from '../../../components/SourcingTable/SourcingTable';
import {SourcingUserListExportButton} from '../../../components/SourcingUserListExportButton/SourcingUserListExportButton';
import {VehiclePreview} from '../../../components/VehiclePreview/VehiclePreview';
import {
  AUCTION_USER_LIST_ORDER_BY_MAP,
  AUCTION_USER_LIST_SORT_OPTIONS,
} from '../../../constants/auctionSortOptions';
import {
  CLASSIFIEDS_USER_LIST_ORDER_BY_MAP,
  CLASSIFIEDS_USER_LIST_SORT_OPTIONS,
} from '../../../constants/classifiedsSortBy';
import {DEFAULT_CURRENCY_CODE} from '../../../constants/DEFAULT_CURRENCY_CODE';
import {USER_LIST_DEFAULT_ORDER_ID} from '../../../constants/USER_LIST_DEFAULT_ORDER_ID';
import {useAuctionEnumLookup} from '../../../hooks/useAuctionEnumLookup';
import {useExportSourcingUserList} from '../../../hooks/useExportSourcingUserList';
import {usePaginatedUserList} from '../../../hooks/usePaginatedUserList';
import {useSortOrderByFromUrl} from '../../../hooks/useSortOrderByFromUrl';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingListAvailability} from '../../../hooks/useSourcingListAvailability';
import {useSourcingPricingStrategy} from '../../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {useSourcingUserListCount} from '../../../hooks/useSourcingUserListCount';
import {SourcingPage} from '../../../types/SourcingPage';
import {getAuctionTableColumns} from '../../../utils/getAuctionTableColumns';
import {getSourcingUserListByPage} from '../../../utils/getSourcingUserListByPage';
import {getSourcingVehicleId} from '../../../utils/getSourcingVehicleId';
import {isAuctionVehicle} from '../../../utils/isAuctionVehicle';

/** Taken from the shared page mapping, so this page and its bulk action drawer export the same list. */
const USER_LIST = getSourcingUserListByPage(SourcingPage.HIDDEN_PAGE);

export function UserHiddenPage() {
  const formatNumber = useFormatNumber();
  const enumLookup = useAuctionEnumLookup();
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const pricingStrategy = useSourcingPricingStrategy();
  const sourcingType = useSourcingType();
  const [classifiedsState, classifiedsDispatch] = useSourcingTable(
    'user-hidden',
    USER_LIST_DEFAULT_ORDER_ID
  );
  const [auctionsState, auctionsDispatch] = useSourcingTable(
    'user-hidden-auctions',
    USER_LIST_DEFAULT_ORDER_ID
  );
  const [state, dispatch] =
    sourcingType === SourcingType.CLASSIFIEDS
      ? [classifiedsState, classifiedsDispatch]
      : [auctionsState, auctionsDispatch];

  const sortByOptions =
    sourcingType === SourcingType.AUCTIONS
      ? AUCTION_USER_LIST_SORT_OPTIONS
      : CLASSIFIEDS_USER_LIST_SORT_OPTIONS;
  const {orderBy: sortOrderByFromUrl, clear: clearUrlSort} = useSortOrderByFromUrl();
  const orderBy =
    sortOrderByFromUrl ??
    (sourcingType === SourcingType.AUCTIONS
      ? AUCTION_USER_LIST_ORDER_BY_MAP.get(state.orderBy)
      : CLASSIFIEDS_USER_LIST_ORDER_BY_MAP.get(state.orderBy));

  const {
    queryResult: tableQueryResult,
    fulfilledTimeStamp,
    listQueryKey,
    isTerminalEmpty,
  } = usePaginatedUserList({
    query: {
      list: 'user-hidden',
      orderBy: isEmpty(orderBy) ? undefined : orderBy?.join(','),
      currency: currency?.code ?? DEFAULT_CURRENCY_CODE,
      buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
      pricingStrategy,
    },
    sourcingType,
    enumLookup,
  });

  const listCount = useSourcingUserListCount(USER_LIST);
  const {exportUserList, isExporting} = useExportSourcingUserList(USER_LIST);
  const handleExport = () => exportUserList({orderBy});

  const sourcingColumns = useSourcingTableColumns();
  const auctionColumns = getAuctionTableColumns({formatNumber, enumLookup});
  const columns = sourcingType === SourcingType.CLASSIFIEDS ? sourcingColumns : auctionColumns;
  const rows = tableQueryResult.data;

  const classifiedsAdIds =
    sourcingType === SourcingType.CLASSIFIEDS ? rows.map((row) => row.sourcingVehicle.adId) : [];
  useSourcingListAvailability({
    adIds: classifiedsAdIds,
    enabled:
      isFeatureEnabled(featureFlags.SOURCING_AVAILABILITY_CHECK) &&
      sourcingType === SourcingType.CLASSIFIEDS,
    userLoadKey: fulfilledTimeStamp ?? 0,
  });

  const selectedAdId = state.selectedRow?.sourcingVehicle.adId;
  const preview = rows.find((row) => row.sourcingVehicle.adId === selectedAdId) ?? head(rows);

  return (
    <SourcingLayout
      hasPreview={isNotNil(preview) || tableQueryResult.isLoading}
      isPreviewLoading={tableQueryResult.isLoading && isEmpty(rows)}
      header={
        <HStack align="center" justify="space-between">
          <Heading size={2} alternative>
            {i18n.t('entity.vehicle.labels.hiddenPage')}
          </Heading>
          <SourcingUserListExportButton
            actionLabel={i18n.t('general.actions.exportAll')}
            vehiclesCount={listCount}
            isExporting={isExporting}
            onClick={handleExport}
            data-testid={testIds.sourcing.classifieds('hidden-exportButton')}
          />
        </HStack>
      }
      content={
        <SourcingDataStatus
          iconUrl="/assets/images/empty_images/empty-hidden.svg"
          text={i18n.t('entity.vehicle.notifications.nothingHidden')}
          isEmpty={isTerminalEmpty}
        >
          <SourcingTable
            state={state}
            dispatch={dispatch}
            columns={columns}
            sortByOptions={sortByOptions}
            queryResult={tableQueryResult}
            listQueryKey={listQueryKey}
            isSortByUnset={isNotNil(sortOrderByFromUrl)}
            onClearUrlSort={clearUrlSort}
            orderBy={orderBy}
          />
        </SourcingDataStatus>
      }
      iframe={
        sourcingType === SourcingType.AUCTIONS && isAuctionVehicle(preview) ? (
          <AuctionPreview key={preview.auctionVehicle.adId} auction={preview} />
        ) : (
          <VehiclePreview
            key={getSourcingVehicleId(preview)}
            vehicleId={getSourcingVehicleId(preview)}
          />
        )
      }
      pageTitle={formatDocumentTitle(
        i18n.t('entity.vehicle.labels.hiddenPage'),
        sourcingType === SourcingType.CLASSIFIEDS
          ? i18n.t('page.sourcing.labels.classifieds')
          : i18n.t('page.sourcing.labels.auctions'),
        i18n.t('page.sourcing.labels.sourcing')
      )}
    />
  );
}
